import { TrashIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import type { FormEvent, ChangeEvent } from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { apiUrl, authFetch, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface Session {
  id: string;
  name: string;
  description: string;
  createdByTeacherId: string;
  createdByTeacherName: string;
  courseId: string;
  courseName: string;
  unitId: string | null;
  unitName: string | null;
  sessionDate: string;
  startTime: string | null;
  endTime: string | null;
  location: string;
  maxStudents: number;
  currentStudents: number;
  isActive: boolean;
  createdDate: string;
  modificationDate: string;
}

interface Course {
  id: string;
  name: string;
  grade: string;
  term: string;
  status: 'active' | 'inactive';
  price: number;
  modificationDate: string;
  description?: string;
  imageUrl?: string;
  isOpenToAll: boolean;
  teacherName?: string;
  subjectName?: string;
}

interface Teacher {
  id: string;
  fullName: string;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
}

interface Unit {
  id: number;
  unitName: string;
  titel: string;
  active: boolean;
  order: number;
  enrollmentDate: string | null;
  creationDate: string;
  lessons: any[];
}

interface CourseTree {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  grade: string;
  description: string;
  imagePath: string | null;
  modificationDate: string;
  enrollmentDate: string | null;
  isOpenToAll: boolean;
  units: Unit[];
}

interface FormData {
  name: string;
  description: string;
  createdByTeacherId: string;
  gradeId: string;
  courseId: string;
  unitId: string | null;
  sessionDate: string;
  location: string;
  maxStudents: string;
}

interface SortConfig {
  key: keyof Session;
  direction: 'asc' | 'desc';
}

const Sessions = () => {
  const navigate = useNavigate();
  const { user, isTeacher, isModerator, getTeacherId } = useUser();
  const [showAddForm, setShowAddForm] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [filters, setFilters] = useState({
    searchText: '',
    createdByTeacherId: '',
    courseId: '',
    dateFrom: '',
    dateTo: '',
    isActive: '',
  });
  const [displayedSessions, setDisplayedSessions] = useState<Session[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    createdByTeacherId: '',
    gradeId: '',
    courseId: '',
    unitId: null,
    sessionDate: '',
    location: '',
    maxStudents: '',
  });

  // Fetch teachers
  const fetchTeachers = async () => {
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeachers(data.data.map((teacher: any) => ({
          id: teacher.id,
          fullName: teacher.fullName
        })));
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  // Fetch grades
  const fetchGrades = async () => {
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    }
  };

  // Fetch courses for selected teacher and grade
  const fetchCourses = async (teacherId?: string, gradeId?: string) => {
    try {
      const teacherIdParam = teacherId || (isTeacher() ? getTeacherId() : null);
      const gradeIdParam = gradeId;
      
      const params = new URLSearchParams();
      params.append('pageNumber', '1');
      params.append('pageSize', '1000');
      
      if (teacherIdParam) {
        params.append('TeacherId', teacherIdParam);
      }
      
      if (gradeIdParam) {
        params.append('GradeId', gradeIdParam);
      }
      
      const url = apiUrl(`Course/all?${params.toString()}`);
        
      const response = await authFetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        const courseData = data.data;
        setCourses(courseData.data.map((course: any) => ({
          id: course.id,
          name: course.courseName,
          grade: course.grade,
          term: course.term,
          status: course.active ? 'active' : 'inactive',
          price: course.price,
          modificationDate: course.modificationDate,
          description: course.description || '',
          imageUrl: course.imagePath || '',
          isOpenToAll: course.isOpenToAll || false,
          teacherName: course.teacherName || 'غير محدد',
          subjectName: course.subjectName || 'غير محدد',
        })));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch units for selected course
  const fetchCourseUnits = async (courseId: string) => {
    if (!courseId) {
      setUnits([]);
      return;
    }

    try {
      const response = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUnits(data.data.units || []);
      }
    } catch (error) {
      console.error('Error fetching course units:', error);
      setUnits([]);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
  const [sessionToToggle, setSessionToToggle] = useState<Session | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Fetch sessions from API on mount
  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      // Add teacher filtering for Teacher role
      const teacherId = isTeacher() ? getTeacherId() : null;
      const teacherParam = teacherId ? `&createdByTeacherId=${teacherId}` : '';
      
      const response = await authFetch(
        apiUrl(`Session?page=${currentPage}&pageSize=${itemsPerPage}${filters.searchText ? `&searchText=${encodeURIComponent(filters.searchText)}` : ''}${filters.createdByTeacherId ? `&createdByTeacherId=${encodeURIComponent(filters.createdByTeacherId)}` : ''}${filters.courseId ? `&courseId=${encodeURIComponent(filters.courseId)}` : ''}${filters.dateFrom ? `&dateFrom=${encodeURIComponent(filters.dateFrom)}` : ''}${filters.dateTo ? `&dateTo=${encodeURIComponent(filters.dateTo)}` : ''}${filters.isActive ? `&isActive=${filters.isActive}` : ''}${teacherParam}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch sessions');
      const data = await response.json();
      
      if (data.success) {
        const sessionData = data.data;
        setTotalItems(sessionData.totalCount);
        setTotalPages(sessionData.totalPages);
        
        setSessions(sessionData.sessions);
        setDisplayedSessions(sessionData.sessions);
      } else {
        throw new Error(data.message || 'Failed to fetch sessions');
      }
    } catch (error) {
      setSessions([]);
      setDisplayedSessions([]);
      setError('Failed to fetch sessions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
    fetchGrades();
    // Don't fetch courses on mount - wait for grade selection
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [currentPage, filters.searchText, filters.createdByTeacherId, filters.courseId, filters.dateFrom, filters.dateTo, filters.isActive]);

  // Auto-select teacher for Teacher role users
  useEffect(() => {
    if (isTeacher() && user && showAddForm && !formData.createdByTeacherId) {
      const teacherId = user.id;
      setFormData(prev => ({ ...prev, createdByTeacherId: teacherId }));
      fetchCourses(teacherId, formData.gradeId);
    }
  }, [user, isTeacher, showAddForm, formData.createdByTeacherId]);

  // Fetch courses when teacher changes (for moderators)
  useEffect(() => {
    if (isModerator() && formData.createdByTeacherId && showAddForm) {
      fetchCourses(formData.createdByTeacherId, formData.gradeId);
    }
  }, [formData.createdByTeacherId, isModerator, showAddForm]);

  // Fetch courses when grade changes
  useEffect(() => {
    if (formData.gradeId && showAddForm) {
      const teacherId = isTeacher() ? getTeacherId() : formData.createdByTeacherId;
      fetchCourses(teacherId || undefined, formData.gradeId);
    } else if (showAddForm && !formData.gradeId) {
      // Clear courses when grade is deselected
      setCourses([]);
    }
  }, [formData.gradeId, showAddForm, isTeacher, getTeacherId]);

  // Fetch units when course changes
  useEffect(() => {
    if (formData.courseId) {
      fetchCourseUnits(formData.courseId);
    } else {
      setUnits([]);
    }
  }, [formData.courseId]);

  // Sort function should only sort the current page's data
  const handleSort = (key: keyof Session) => {
    setSortConfig((prevSortConfig) => {
      const direction = prevSortConfig?.key === key && prevSortConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  // Apply sorting to the current page of data
  const sortedSessions = useMemo(() => {
    if (!sortConfig) return displayedSessions;
    
    return [...displayedSessions].sort((a, b) => {
      const key = sortConfig.key;
      const direction = sortConfig.direction;
      
      // Use type assertion to avoid TypeScript errors
      const aValue = a[key] as string | number | boolean;
      const bValue = b[key] as string | number | boolean;
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [displayedSessions, sortConfig]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      searchText: '',
      createdByTeacherId: '',
      courseId: '',
      dateFrom: '',
      dateTo: '',
      isActive: '',
    });
    setCurrentPage(1); // Reset to first page
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Reset unit when course changes
      ...(name === 'courseId' && { unitId: null }),
      // Reset course and unit when grade changes
      ...(name === 'gradeId' && { courseId: '', unitId: null })
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      // Validate required fields (course is now optional)
      if (!formData.name || !formData.sessionDate) {
        alert('يرجى ملء جميع الحقول المطلوبة');
        return;
      }

      const response = await authFetch(apiUrl('Session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          createdByTeacherId: formData.createdByTeacherId,
          gradeId: formData.gradeId || null,
          courseId: formData.unitId ? null : (formData.courseId || null),
          unitId: formData.unitId || null,
          sessionDate: formData.sessionDate,
          location: formData.location,
          maxStudents: parseInt(formData.maxStudents) || 0
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh the session list after successful addition
        setCurrentPage(1); // Go back to first page
        setShowAddForm(false);
        setFormData({
          name: '',
          description: '',
          createdByTeacherId: '',
          gradeId: '',
          courseId: '',
          unitId: null,
          sessionDate: '',
          location: '',
          maxStudents: '',
        });
        fetchSessions();
      } else {
        alert('Failed to add session: ' + data.message);
      }
    } catch (error) {
      console.error('Error adding session:', error);
      alert('Failed to add session. Please try again later.');
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    
    try {
      const response = await authFetch(apiUrl(`Session/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If API call succeeds, update the local state
        setSessions(sessions.filter((session: Session) => session.id !== id));
        setDisplayedSessions(displayedSessions.filter((session: Session) => session.id !== id));
        setSessionToDelete(null);
      } else {
        alert('Failed to delete session: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Failed to delete session. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (session: Session) => {
    try {
      const response = await authFetch(apiUrl(`Session/${session.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          id: session.id,
          name: session.name,
          description: session.description,
          createdByTeacherId: session.createdByTeacherId,
          courseId: session.courseId,
          unitId: session.unitId,
          sessionDate: session.sessionDate,
          location: session.location,
          isActive: !session.isActive
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If successful, update the local state and close the modal
        setSessions(
          sessions.map((s: Session) =>
            s.id === session.id
              ? {
                  ...s,
                  isActive: !s.isActive,
                }
              : s
          )
        );
        setDisplayedSessions(
          displayedSessions.map((s: Session) =>
            s.id === session.id
              ? {
                  ...s,
                  isActive: !s.isActive,
                }
              : s
          )
        );
        setSessionToToggle(null);
      } else {
        alert('Failed to update session status: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating session status:', error);
      alert('Failed to update session status. Please try again later.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                محاضرات السنتر
              </h1>
              <p className="mt-2 text-gray-600">إدارة محاضرات السنتر</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                         shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              إضافة محاضرة جديدة
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <label htmlFor="search-filter" className="block text-sm font-medium text-gray-700">
                البحث
              </label>
              <input
                type="text"
                id="search-filter"
                value={filters.searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, searchText: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="البحث في المحاضرات..."
              />
            </div>
            
            {/* Teacher Filter - Only for Moderators */}
            {isModerator() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                <select
                  value={filters.createdByTeacherId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, createdByTeacherId: e.target.value })}
                  className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="">جميع المدرسين</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.fullName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Teacher Info Display - Only for Teachers */}
            {isTeacher() && user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                <div className="block w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                  {user.name}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="course-filter" className="block text-sm font-medium text-gray-700">
                الدورة
              </label>
              <select
                id="course-filter"
                value={filters.courseId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, courseId: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع الدورات</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label htmlFor="date-from-filter" className="block text-sm font-medium text-gray-700">
                من تاريخ
              </label>
              <input
                type="datetime-local"
                id="date-from-filter"
                value={filters.dateFrom}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="date-to-filter" className="block text-sm font-medium text-gray-700">
                إلى تاريخ
              </label>
              <input
                type="datetime-local"
                id="date-to-filter"
                value={filters.dateTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateTo: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">
                الحالة
              </label>
              <select
                id="status-filter"
                value={filters.isActive}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, isActive: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع الحالات</option>
                <option value="true">نشط</option>
                <option value="false">غير نشط</option>
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    اسم المحاضرة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    المدرس
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الدورة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الوحدة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    التاريخ
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الموقع
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : sortedSessions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على محاضرات</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => navigate(`/sessions/${session.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{session.name}</div>
                        {session.description && (
                          <div className="text-sm text-gray-500 mt-1">{session.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{session.createdByTeacherName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{session.courseName || 'غير محدد'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{session.unitName || 'غير محدد'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{formatDate(session.sessionDate)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{session.location}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                          ${session.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'}`}
                        >
                          {session.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-reverse space-x-2">
                          <button
                            onClick={() => setSessionToToggle(session)}
                            className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                              session.isActive
                                ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={session.isActive ? 'إخفاء المحاضرة' : 'إظهار المحاضرة'}
                          >
                            {session.isActive ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => setSessionToDelete(session)}
                            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                     transition-colors duration-200"
                            title="حذف المحاضرة"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && displayedSessions.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white rounded-2xl shadow-lg px-6 py-4">
            <div className="text-sm text-gray-700 mb-4 sm:mb-0">
              عرض <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> إلى{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>{' '}
              من <span className="font-medium">{totalItems}</span> نتيجة
            </div>
            <div className="flex items-center space-x-reverse space-x-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأول
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  if (index > 0 && prevPage && page - prevPage > 1) {
                    return [
                      <span key={`ellipsis-${prevPage}`} className="px-3 py-2">...</span>,
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                          ${currentPage === page
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                          }`}
                      >
                        {page}
                      </button>
                    ];
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                        ${currentPage === page
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأخير
              </button>
            </div>
          </div>
        )}

        {/* Add Session Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة محاضرة جديدة</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Teacher Selection - Only for Moderators */}
                  {isModerator() && (
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        المدرس
                      </label>
                      <select
                        name="createdByTeacherId"
                        value={formData.createdByTeacherId}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      >
                        <option value="">اختر المدرس</option>
                        {teachers.map(teacher => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.fullName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Teacher Info Display - Only for Teachers */}
                  {isTeacher() && user && (
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">المدرس</label>
                      <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                        {user.name}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الصف الدراسي
                    </label>
                    <select
                      name="gradeId"
                      value={formData.gradeId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">اختر الصف الدراسي</option>
                      {grades.map(grade => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      اسم المحاضرة
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      name="name"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل اسم المحاضرة"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      وصف المحاضرة
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={handleInputChange}
                      name="description"
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                      placeholder="أدخل وصف المحاضرة"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الدورة (اختياري)
                    </label>
                    <select
                      name="courseId"
                      value={formData.courseId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">اختر الدورة (اختياري)</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.courseId && (
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        الوحدة (اختياري)
                      </label>
                      <select
                        name="unitId"
                        value={formData.unitId || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">اختر الوحدة (اختياري)</option>
                        {units.map(unit => (
                          <option key={unit.id} value={unit.id}>
                            {unit.unitName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      تاريخ المحاضرة
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.sessionDate}
                      onChange={handleInputChange}
                      name="sessionDate"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الموقع
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={handleInputChange}
                      name="location"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="أدخل موقع المحاضرة"
                    />
                  </div>

                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    إضافة المحاضرة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {sessionToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">حذف المحاضرة</h3>
                <p className="text-gray-600">
                  هل أنت متأكد من حذف <span className="font-bold text-gray-900">{sessionToDelete.name}</span>؟
                  لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setSessionToDelete(null)}
                  disabled={isDeleting}
                >
                  إلغاء
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center gap-2"
                  onClick={() => handleDelete(sessionToDelete.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      حذف
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {sessionToToggle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {sessionToToggle.isActive ? 'إخفاء المحاضرة' : 'إظهار المحاضرة'}
                </h3>
                <p className="text-gray-600">
                  هل أنت متأكد من أنك تريد {sessionToToggle.isActive ? 'إخفاء' : 'إظهار'} المحاضرة <span className="font-bold text-gray-900">{sessionToToggle.name}</span>؟
                  {sessionToToggle.isActive ? ' هذا سيجعلها غير مرئية للطلاب.' : ' هذا سيجعلها مرئية للطلاب.'}
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setSessionToToggle(null)}
                >
                  إلغاء
                </button>
                <button
                  className={`px-4 py-2 rounded-lg ${
                    sessionToToggle.isActive
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    sessionToToggle.isActive
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  }`}
                  onClick={() => handleToggleStatus(sessionToToggle)}
                >
                  {sessionToToggle.isActive ? 'إخفاء المحاضرة' : 'إظهار المحاضرة'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sessions;
